import { Dispatch, Store } from 'redux';
import { getIsEnableSquareCreditCardCheckoutFF } from '../selectors/app';
import { getEventId } from '../selectors/event';
import {
    getHasSqAnalyticsToken,
    getSquareAnalytics,
} from '../selectors/squareToken';
import { CheckoutState } from '../state';
import { TokensNamespace } from '../state/tokens';

export const UPDATE_WAITING_ROOM_TOKEN = 'updateWaitingRoomToken';
export const UPDATE_GUEST_TOKEN = 'updateGuestToken';
export const UPDATE_ORDER_TOKEN = 'updateOrderToken';
export const RESET_GUEST_TOKEN = 'resetGuestToken';
export const RESET_ORDER_TOKEN = 'resetOrderToken';
export const RESET_WAITING_ROOM_TOKEN = 'resetWaitingRoomToken';
export const UPDATE_SQUARE_ANALYTICS_TOKEN = 'updateSquareAnalyticsToken';

const _updateWaitingRoomToken = (data: { waitingRoomToken: string }) => ({
    type: UPDATE_WAITING_ROOM_TOKEN,
    payload: data,
});

const _updateGuestToken = (data: { guestToken: string }) => ({
    type: UPDATE_GUEST_TOKEN,
    payload: data,
});

const _updateOrderToken = (data: { orderToken: string }) => ({
    type: UPDATE_ORDER_TOKEN,
    payload: data,
});

const _resetGuestToken = () => ({
    type: RESET_GUEST_TOKEN,
});

const _resetOrderToken = () => ({
    type: RESET_ORDER_TOKEN,
});

const _resetWaitingRoomToken = () => ({
    type: RESET_WAITING_ROOM_TOKEN,
});

const _updateSquareAnalyticsToken = (data: {
    sqAnalyticsToken: string | null;
}) => ({
    type: UPDATE_SQUARE_ANALYTICS_TOKEN,
    payload: data,
});

/**
 * We keep a copy of the tokens portion of the state store here and
 * export the accessor method getTokens so api functions, which don't
 * have access to getState can get at the tokens. Maintaining state
 * outside of the store is an antipattern so try not to use this
 * approach elsewhere.
 * TODO: EB-130741 [antipattern] header tokens in checkoutWidget are saved locally, apart from the state
 */
let tokens: Partial<TokensNamespace> = {};
let storeInstance: Store<CheckoutState> | undefined;

export const getTokens = () => tokens;

export const getStoredEventId = (): string | undefined => {
    if (storeInstance) {
        const state = storeInstance.getState();
        return getEventId(state);
    }
    return undefined;
};

export const setStoreInstance = (store: Store<CheckoutState>) => {
    storeInstance = store;
};

export const setTokensFromState = (state?: CheckoutState) => {
    if (state) {
        tokens = state.tokens;
    }
};

export const updateTokensWithWaitingRoomToken =
    (waitingRoomToken: string) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        if (waitingRoomToken) {
            dispatch(
                _updateWaitingRoomToken({
                    waitingRoomToken,
                }),
            );
        }
        tokens = getState().tokens;
        return Promise.resolve();
    };

export const updateTokensWithGuestToken =
    (guestToken: string) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        if (guestToken) {
            dispatch(
                _updateGuestToken({
                    guestToken,
                }),
            );
        }
        tokens = getState().tokens;
        return Promise.resolve();
    };

export const updateTokensWithOrderToken =
    (orderToken: string) =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        if (orderToken) {
            dispatch(
                _updateOrderToken({
                    orderToken,
                }),
            );
        }
        tokens = getState().tokens;
        return Promise.resolve();
    };

export const resetGuestToken =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        dispatch(_resetGuestToken());
        tokens = getState().tokens;
        return Promise.resolve();
    };

export const resetOrderToken =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        dispatch(_resetOrderToken());
        tokens = getState().tokens;
        return Promise.resolve();
    };

export const resetWaitingRoomToken =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        dispatch(_resetWaitingRoomToken());
        tokens = getState().tokens;
        return Promise.resolve();
    };

export const generateSquareToken =
    () =>
    (dispatch: Dispatch<CheckoutState>, getState: () => CheckoutState) => {
        const state = getState();
        if (getIsEnableSquareCreditCardCheckoutFF(state)) {
            if (!getHasSqAnalyticsToken(state)) {
                const squareAnalytics = getSquareAnalytics(state);

                squareAnalytics.getToken((sqAnalyticsToken) =>
                    dispatch(_updateSquareAnalyticsToken({ sqAnalyticsToken })),
                );
            }
        }
    };

export const handleAnalyticsTokenOnError = (
    dispatch: Dispatch<CheckoutState>,
    getState: () => CheckoutState,
) => {
    if (getIsEnableSquareCreditCardCheckoutFF(getState())) {
        dispatch(_updateSquareAnalyticsToken({ sqAnalyticsToken: null }));
        dispatch(generateSquareToken());
    }
};
